import React, { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { useUserAuth } from '../../context/userAuthContext';

// Traducciones para el componente PartnersSection
const translations = {
  en: {
    heading: 'SUCCESS STORIES',
    title: 'Enterprise Solutions in Action',
    subtitle: 'Discover how our technology solutions drive measurable business outcomes for industry leaders.',
    keyResults: 'Measurable Outcomes',
    viewFullCase: 'View case study',
    caseStudy: 'CASE STUDY',
    partners: [
      {
        name: "RadioSistemas",
        logo: "https://firebasestorage.googleapis.com/v0/b/radiosistemas-a46cd.appspot.com/o/LogoRS.png?alt=media&token=0132c19f-8a46-4d45-8166-6905d743da2d",
        domain: "radiosistemas.mx",
        description: "Leading company in public and private security, radio communication infrastructure and cybersecurity.",
        results: [
          "40% increase in early threat detection with FroniosAI",
          "65% reduction in false positives in security systems",
          "Implementation of advanced encryption across the entire communications network"
        ],
        industry: "Security & Communications"
      },
      {
        name: "LiveSpeed",
        logo: "https://firebasestorage.googleapis.com/v0/b/mtbking-aa657.appspot.com/o/LiveSpeed.png?alt=media&token=f30dbc3b-6932-486d-b992-3a705f04f0b5",
        domain: "livespeed.mx",
        description: "Social network and real-time timing system for sports events and professional races.",
        results: [
          "Real-time data processing for more than 10,000 simultaneous participants",
          "Predictive performance analysis with 92% accuracy",
          "78% reduction in system latency during massive events"
        ],
        industry: "Sports & Entertainment"
      },
      {
        name: "Radeo.mx",
        logo: "https://firebasestorage.googleapis.com/v0/b/radiosistemas-a46cd.appspot.com/o/radeo%2Fradeowhitelogo_797098c4-4b83-40c9-b29a-df23598ebe43%20(1).webp?alt=media&token=e5808804-10d5-4f15-a1c7-6f4c36c0d5b6",
        domain: "radeo.mx",
        description: "Innovative e-commerce platform specializing in electronics, tech products, and communication equipment.",
        results: [
          "45% increase in conversion rate using AI-powered product recommendations",
          "Customer support automation solving 68% of queries without human intervention",
          "Inventory forecasting with 93% accuracy reducing stockouts by 37%"
        ],
        industry: "E-commerce & Retail"
      }
    ]
  },
  es: {
    heading: 'CASOS DE ÉXITO',
    title: 'Soluciones Empresariales en Acción',
    subtitle: 'Descubra cómo nuestras soluciones tecnológicas generan resultados medibles para líderes de la industria.',
    keyResults: 'Resultados Medibles',
    viewFullCase: 'Ver estudio de caso',
    caseStudy: 'CASO DE ESTUDIO',
    partners: [
      {
        name: "RadioSistemas",
        logo: "https://firebasestorage.googleapis.com/v0/b/radiosistemas-a46cd.appspot.com/o/LogoRS.png?alt=media&token=0132c19f-8a46-4d45-8166-6905d743da2d",
        domain: "radiosistemas.mx",
        description: "Empresa líder en seguridad pública y privada, infraestructura de radiocomunicación y ciberseguridad.",
        results: [
          "Incremento del 40% en detección temprana de amenazas con FroniosAI",
          "Reducción del 65% en falsos positivos en sistemas de seguridad",
          "Implementación de encriptación avanzada en toda la red de comunicaciones"
        ],
        industry: "Seguridad y Comunicaciones"
      },
      {
        name: "LiveSpeed",
        logo: "https://firebasestorage.googleapis.com/v0/b/mtbking-aa657.appspot.com/o/LiveSpeed.png?alt=media&token=f30dbc3b-6932-486d-b992-3a705f04f0b5",
        domain: "livespeed.mx",
        description: "Red social y sistema de cronometraje en tiempo real para eventos deportivos y carreras profesionales.",
        results: [
          "Procesamiento en tiempo real de datos para más de 10,000 participantes simultáneos",
          "Análisis predictivo de rendimiento con precisión del 92%",
          "Reducción del 78% en latencia del sistema durante eventos masivos"
        ],
        industry: "Deportes y Entretenimiento"
      },
      {
        name: "Radeo.mx",
        logo: "https://firebasestorage.googleapis.com/v0/b/radiosistemas-a46cd.appspot.com/o/radeo%2Fradeowhitelogo_797098c4-4b83-40c9-b29a-df23598ebe43%20(1).webp?alt=media&token=e5808804-10d5-4f15-a1c7-6f4c36c0d5b6",
        domain: "radeo.mx",
        description: "Plataforma innovadora de comercio electrónico especializada en electrónica, productos tecnológicos y equipos de comunicación.",
        results: [
          "Aumento del 45% en tasa de conversión utilizando recomendaciones de productos impulsadas por IA",
          "Automatización de soporte al cliente resolviendo el 68% de consultas sin intervención humana",
          "Pronóstico de inventario con 93% de precisión reduciendo agotamiento de existencias en un 37%"
        ],
        industry: "E-commerce y Retail"
      }
    ]
  }
};

const PartnersSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { currentLanguage } = useUserAuth();
  const t = translations[currentLanguage];

  return (
    <div className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Encabezado mejorado con línea decorativa */}
        <div className="mb-12 text-center">
          <div className="inline-block relative">
            <h3 className="text-sm font-bold tracking-wider text-blue-700 uppercase mb-2">{t.heading}</h3>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-0.5 bg-blue-500"></div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mt-4 mb-3">
            {t.title}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Navegación de pestañas mejorada y más responsiva */}
        <div className="flex flex-wrap justify-center mb-10 border-b border-gray-200">
          {t.partners.map((partner, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`py-3 px-4 md:px-6 text-sm font-medium transition-colors focus:outline-none relative mb-1 
                ${activeTab === index
                  ? "text-blue-700"
                  : "text-gray-500 hover:text-gray-800"
                }`}
            >
              {partner.name}
              {activeTab === index && (
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600"></span>
              )}
            </button>
          ))}
        </div>

        {/* Contenido de partners rediseñado para mejor responsividad */}
        <div className="space-y-8">
          {/* Información principal del partner con logo */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="p-6">
              {/* Logo e información del partner */}
              <div className="flex items-center pb-5 mb-5 border-b border-gray-100">
                <div className="w-16 h-16 bg-gray-50 rounded-md border border-gray-200 flex items-center justify-center mr-4 p-2">
                  <img
                    src={t.partners[activeTab].logo}
                    alt={`${t.partners[activeTab].name} logo`}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div>
                  <span className="text-xs font-medium text-blue-700 block mb-1">
                    {t.partners[activeTab].industry}
                  </span>
                  <h3 className="text-xl font-bold text-gray-800">{t.partners[activeTab].name}</h3>
                  <a href={`https://${t.partners[activeTab].domain}`} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:underline">
                    {t.partners[activeTab].domain}
                  </a>
                </div>
              </div>

              {/* Descripción */}
              <p className="text-gray-700 mb-6 text-sm leading-relaxed">
                {t.partners[activeTab].description}
              </p>

              {/* Enlace al caso de estudio */}
              <a
                href="#"
                className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
              >
                <span>{t.viewFullCase}</span>
                <ArrowRight className="h-4 w-4 ml-1" />
              </a>
            </div>
          </div>

          {/* Resultados medibles */}
          <div className="bg-gradient-to-br from-blue-50 to-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="p-6">
              <h4 className="text-lg font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200">
                {t.keyResults}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {t.partners[activeTab].results.map((result, index) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start">
                      <div className="bg-blue-50 rounded-full p-2 mr-3 mt-1">
                        <Check className="h-4 w-4 text-blue-600" />
                      </div>
                      <p className="text-gray-700 text-sm">
                        {result}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Imagen del caso de estudio para móvil y vista compacta */}
          <div className="lg:hidden">
            <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100 relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/80 via-gray-900/60 to-transparent z-10"></div>
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url('/images/${t.partners[activeTab].name.toLowerCase().replace('.mx', '')}-case.jpg')`,
                }}
              ></div>
              <div className="absolute bottom-0 left-0 p-4 z-20">
                <p className="text-xs font-bold text-blue-300 mb-1">{t.caseStudy}</p>
                <h3 className="text-lg font-bold text-white">
                  {t.partners[activeTab].name}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnersSection;